import type { Resident } from '@lmc/ts-common'
import { FormItem, Heading, TextArea } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Form, Modal } from 'antd'
import { showToast } from 'common/utils'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { NAMESPACE } from 'locale'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useCurrentStock } from '../../hooks/useCurrentStock'
import useMedicationSchedulePatternDiscontinue from '../../hooks/useMedicationSchedulePatternDiscontinue'
import { getMedicationIcon } from '../../utils/medicationUtils'
import DisposalModal from './DisposalModal'
import MedicationStillInStockModal from './MedicationStillInStockModal'

interface Medication {
  id: string
  name: string
}

type DiscontinueMedicationModalProps = {
  open: boolean
  medication: Medication
  resident: Resident
  residentMedicationId?: string
  onCancel: () => void
  onSuccess?: () => void
}

const DiscontinueMedicationModal = ({
  open,
  medication,
  resident,
  residentMedicationId,
  onCancel,
  onSuccess
}: DiscontinueMedicationModalProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const [form] = Form.useForm()
  const [stockModalOpen, setStockModalOpen] = useState(false)
  const [disposalModalOpen, setDisposalModalOpen] = useState(false)
  const [pendingDiscontinueReason, setPendingDiscontinueReason] = useState<
    string | null
  >(null)
  const sendEvent = useMixpanelEvent()

  const effectiveResidentMedicationId = residentMedicationId || medication.id

  const { currentStock } = useCurrentStock({
    residentMedicationId: effectiveResidentMedicationId,
    residentId: resident.id,
    enabled: open && !!effectiveResidentMedicationId
  })

  const {
    discontinueMedicationSchedulePattern,
    isDiscontinuingMedicationSchedulePattern
  } = useMedicationSchedulePatternDiscontinue({
    medicationId: medication.id,
    onSuccess: () => {
      showToast({
        type: 'success',
        title: t('MEDICATION_HAS_BEEN_DISCONTINUED', {
          medication: medication.name
        })
      })
      form.resetFields()
      onSuccess?.()
    },
    onError: () => {
      showToast({
        type: 'error',
        title: t('DISCONTINUE_MEDICATION_ERROR')
      })
      handleCancel()
    }
  })

  const performDiscontinue = async (reason: string) => {
    await discontinueMedicationSchedulePattern(reason)
  }

  const handleDiscontinue = async () => {
    try {
      const values = await form.validateFields()

      if (effectiveResidentMedicationId && currentStock) {
        const stockLevel = currentStock.currentStockLevel ?? 0
        if (stockLevel > 0) {
          setPendingDiscontinueReason(values.reasonDiscontinued)
          setStockModalOpen(true)
          return
        }
      }

      await performDiscontinue(values.reasonDiscontinued)
    } catch (error) {
      // Validation failed
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setPendingDiscontinueReason(null)
    onCancel()
  }

  const handleStockModalCancel = async () => {
    setStockModalOpen(false)
    const currentStockLevel = currentStock?.currentStockLevel ?? 0
    sendEvent(MixpanelEvents.EMAR_V2_DISPOSE_LATER_CLICKED_FROM_DISCONTINUE, {
      medicationName: medication.name,
      residentId: resident.id,
      stockLevel: currentStockLevel,
      time: new Date().toISOString()
    })
    if (pendingDiscontinueReason) {
      await performDiscontinue(pendingDiscontinueReason)
      setPendingDiscontinueReason(null)
    }
  }

  const handleDispose = () => {
    setStockModalOpen(false)
    setDisposalModalOpen(true)
    const currentStockLevel = currentStock?.currentStockLevel ?? 0
    sendEvent(MixpanelEvents.EMAR_V2_DISPOSAL_MODAL_OPENED_FROM_DISCONTINUE, {
      medicationName: medication.name,
      residentId: resident.id,
      stockLevel: currentStockLevel,
      time: new Date().toISOString()
    })
  }

  const handleDisposalCancel = () => {
    setDisposalModalOpen(false)
    setStockModalOpen(true)
  }

  const handleDisposalSuccess = async () => {
    setDisposalModalOpen(false)
    if (pendingDiscontinueReason) {
      await performDiscontinue(pendingDiscontinueReason)
      setPendingDiscontinueReason(null)
    }
  }

  const residentFullName = `${resident.name.first} ${resident.name.last}`
  const stockLevel = currentStock?.currentStockLevel ?? 0
  const stockUnit = currentStock?.unit || ''

  return (
    <>
      <Modal
        open={open}
        onCancel={handleCancel}
        footer={
          <FooterContainer>
            <ButtonsContainer>
              <Button onClick={handleCancel}>{t('CANCEL')}</Button>
              <Button
                variant="danger"
                onClick={handleDiscontinue}
                isLoading={isDiscontinuingMedicationSchedulePattern}
              >
                {t('DISCONTINUE_MEDICATION')}
              </Button>
            </ButtonsContainer>
          </FooterContainer>
        }
        width={630}
        title={
          <TitleContainer>
            {getMedicationIcon('discontinued')}
            <ModalTitle>
              <Heading level={3}>{t('DISCONTINUE_MEDICATION')}</Heading>
            </ModalTitle>
          </TitleContainer>
        }
      >
        <Divider />

        <Form form={form} preserve={false} layout="vertical">
          <ModalContentContainer>
            <Text>
              <Trans
                i18nKey="ARE_YOU_SURE_DISCONTINUE_MEDICATION"
                ns={NAMESPACE.EMAR_V2}
                values={{
                  medication: medication.name,
                  name: residentFullName
                }}
                components={{ strong: <strong /> }}
              />
            </Text>

            <FormItem
              name="reasonDiscontinued"
              label={<Text>{t('REASON_FOR_CHANGE')}</Text>}
              required
              full
              rules={[
                {
                  required: true,
                  message: t('REASON_FOR_CHANGE_REQUIRED')
                }
              ]}
            >
              <TextArea rows={2} placeholder={t('TYPE_HERE')} />
            </FormItem>

            <NoteContainer>
              <NoteText level={2}>{t('DISCONTINUE_MEDICATION_NOTE')}</NoteText>
            </NoteContainer>
          </ModalContentContainer>
        </Form>

        <Divider />
      </Modal>

      {stockModalOpen && (
        <MedicationStillInStockModal
          open={stockModalOpen}
          medicationName={medication.name}
          quantity={stockLevel}
          stockUnit={stockUnit}
          onCancel={handleStockModalCancel}
          onDispose={handleDispose}
        />
      )}

      {disposalModalOpen && effectiveResidentMedicationId && (
        <DisposalModal
          open={disposalModalOpen}
          medicationName={medication.name}
          expectedStock={stockLevel}
          residentId={resident.id}
          residentMedicationId={effectiveResidentMedicationId}
          unit={stockUnit}
          onCancel={handleDisposalCancel}
          onSuccess={handleDisposalSuccess}
          prefilledDescription={t('DISPOSAL_OF_DISCONTINUED_MEDICATION')}
          prefilledDisposedStock={stockLevel}
        />
      )}
    </>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ModalTitle = styled.div`
  text-transform: none;
  font-weight: normal;
`

const ModalContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
`

const NoteContainer = styled.div`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  background: ${({ theme }) => theme.color.neutral.color10};
  padding: 12px;
`

const NoteText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const FooterContainer = styled.div`
  padding-top: 12px;
  margin-bottom: 12px;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`

export default DiscontinueMedicationModal
